import { lazy } from 'react';
import type { ElementType } from 'react';

import {
  canAddEmail,
  canOpenSettings,
  isAdmin,
  isAuthenticated,
  isOnboarding,
  isUser,
} from '@/entities/session';
import type { AuthState } from '@/entities/session';
import { CheckoutSuccessPage } from '@/pages/checkout-success';
import { LoginPage } from '@/pages/login';
import { SignUpPage } from '@/pages/sign-up';

import { HomeRedirect } from './RouteGuards';

const SignUpConfirmPage = lazy(() =>
  import('@/pages/sign-up-confirm').then((m) => ({
    default: m.SignUpConfirmPage,
  })),
);
const CheckMailboxPage = lazy(() =>
  import('@/pages/check-mailbox').then((m) => ({
    default: m.CheckMailboxPage,
  })),
);
const InvitationPage = lazy(() =>
  import('@/pages/invitation').then((m) => ({ default: m.InvitationPage })),
);
const RestorePasswordPage = lazy(() =>
  import('@/pages/restore-password').then((m) => ({
    default: m.RestorePasswordPage,
  })),
);
const VerifyCodePage = lazy(() =>
  import('@/pages/verify-code').then((m) => ({ default: m.VerifyCodePage })),
);
const NewPasswordPage = lazy(() =>
  import('@/pages/new-password').then((m) => ({ default: m.NewPasswordPage })),
);
const SuccessPage = lazy(() =>
  import('@/pages/success').then((m) => ({ default: m.SuccessPage })),
);
const ExpiredPage = lazy(() =>
  import('@/pages/expired').then((m) => ({ default: m.ExpiredPage })),
);
const AlreadyRegisteredPage = lazy(() =>
  import('@/pages/already-registered').then((m) => ({
    default: m.AlreadyRegisteredPage,
  })),
);
const OnBoardingPlanNew = lazy(() =>
  import('@/pages/onboarding-plan').then((m) => ({
    default: m.OnboardingPlanPage,
  })),
);
const OnBoardingCheckout = lazy(() =>
  import('@/pages/onboarding-checkout').then((m) => ({
    default: m.OnboardingCheckoutPage,
  })),
);
const DashboardPage = lazy(() =>
  import('@/pages/dashboard').then((m) => ({ default: m.DashboardPage })),
);
const SettingPage = lazy(() =>
  import('@/pages/settings').then((m) => ({ default: m.SettingsPage })),
);
const UsersAdminPage = lazy(() =>
  import('@/pages/admin-users').then((m) => ({ default: m.AdminUsersPage })),
);

export const LAYOUTS = {
  external: 'external',
  internal: 'internal',
};

// Route options:
//   guest  - only for signed-out users, signed-in ones are sent to their home page
//   access - rule from @/entities/session (lib/access.ts), a failed check sends the user to their home page
//   layout - LAYOUTS.external (lazy page without app chrome) or LAYOUTS.internal
//            (header + sidebar); omit for eagerly loaded standalone pages
// Routes with neither `guest` nor `access` are open to everyone.
export interface RouteConfig {
  path: string;
  Component: ElementType;
  guest?: boolean;
  access?: (auth: AuthState) => boolean;
  layout?: string;
}

export const routes: RouteConfig[] = [
  { path: '/', Component: HomeRedirect },
  { path: '/login', Component: LoginPage, guest: true },
  { path: '/sign-up', Component: SignUpPage, guest: true },
  { path: '/add-email', Component: SignUpPage, access: canAddEmail },
  {
    path: '/onboarding/checkout/success',
    Component: CheckoutSuccessPage,
    access: isUser,
  },

  {
    path: '/check-mailbox',
    Component: CheckMailboxPage,
    guest: true,
    layout: LAYOUTS.external,
  },
  {
    path: '/sign-up/confirm/:hash',
    Component: SignUpConfirmPage,
    layout: LAYOUTS.external,
  },
  {
    path: '/invitation/:code',
    Component: InvitationPage,
    layout: LAYOUTS.external,
  },
  {
    path: '/restore-password',
    Component: RestorePasswordPage,
    guest: true,
    layout: LAYOUTS.external,
  },
  {
    path: '/verify-code',
    Component: VerifyCodePage,
    guest: true,
    layout: LAYOUTS.external,
  },
  {
    path: '/restore-password/:hash',
    Component: NewPasswordPage,
    layout: LAYOUTS.external,
  },
  { path: '/success/:hash', Component: SuccessPage, layout: LAYOUTS.external },
  { path: '/success', Component: SuccessPage, layout: LAYOUTS.external },
  {
    path: '/expired',
    Component: ExpiredPage,
    guest: true,
    layout: LAYOUTS.external,
  },
  {
    path: '/already-registered',
    Component: AlreadyRegisteredPage,
    guest: true,
    layout: LAYOUTS.external,
  },
  {
    path: '/onboarding/plan',
    Component: OnBoardingPlanNew,
    access: isOnboarding,
    layout: LAYOUTS.external,
  },
  {
    path: '/onboarding/checkout/:mode/:intentId',
    Component: OnBoardingCheckout,
    access: isAuthenticated,
    layout: LAYOUTS.external,
  },
  {
    path: '/onboarding/checkout/:mode',
    Component: OnBoardingCheckout,
    access: isAuthenticated,
    layout: LAYOUTS.external,
  },

  {
    path: '/dashboard',
    Component: DashboardPage,
    access: isUser,
    layout: LAYOUTS.internal,
  },
  {
    path: '/admin/customers',
    Component: UsersAdminPage,
    access: isAdmin,
    layout: LAYOUTS.internal,
  },
  {
    path: '/settings/payment/:intentId',
    Component: SettingPage,
    access: canOpenSettings,
    layout: LAYOUTS.internal,
  },
  {
    path: '/settings/:hash',
    Component: SettingPage,
    access: canOpenSettings,
    layout: LAYOUTS.internal,
  },
  {
    path: '/settings',
    Component: SettingPage,
    access: canOpenSettings,
    layout: LAYOUTS.internal,
  },
];
